import React, { useState, useEffect, useRef } from 'react';
import { ApiServiceAdmin } from '../services/ApiServiceAdmin';
import { currentOffsetOf, FIXED_OFFSET_OPTIONS, getIanaTimezones } from '../utils/timezones';
import { formatTimeInTz } from '../utils/dateTime';
import { formatParamValue } from '../utils/paramDisplay';
import { COUNTRIES } from '../constants/countries';
import { SchoolYearSelector } from './SchoolYearSelector';
import { SchoolYearSystemInfo } from './SchoolYearSystemInfo';
import { SchoolYearCard, SchoolYearStatusChip, schoolYearFieldStyle } from './SchoolYearCard';
import { useSchoolYear } from '../contexts/SchoolYearContext';

interface Param {
  id?: number;
  name: string;
  value: string;
  description?: string;
  type?: string;
}

interface Props {
  isRTL: boolean;
  currentLanguage: string;
}

const api = ApiServiceAdmin.getInstance();
const L = (isRTL: boolean, fr: string, ar: string) => (isRTL ? ar : fr);

/** Masqués du tableau : gérés ailleurs (année scolaire). */
const HIDDEN_FROM_TABLE = new Set(['currentSchoolYearId']);

/** Labels traduits pour chaque clé technique connue. */
const PARAM_NAMES: Record<string, { ar: string; fr: string }> = {
  targetRepeatNewHifzCount:     { ar: 'تكرار الحفظ الجديد',                         fr: 'Répétitions mémorisation (nouveau)' },
  maxPreviousSessions:          { ar: 'الحد الأقصى لعرض الجلسات السابقة',            fr: 'Nombre max de sessions précédentes affichées' },
  repeatOldHifzCount:           { ar: 'تكرار الحفظ القديم',                          fr: 'Répétitions mémorisation (ancien)' },
  repeatReviewCount:            { ar: 'تكرار المراجعة',                              fr: 'Répétitions révision' },
  dailyGoal:                    { ar: 'الهدف اليومي',                                fr: 'Objectif quotidien' },
  weeklyGoal:                   { ar: 'الهدف الأسبوعي',                              fr: 'Objectif hebdomadaire' },
  monthlyGoal:                  { ar: 'الهدف الشهري',                               fr: 'Objectif mensuel' },
  allowTeacherToAddExamPoints:  { ar: 'السماح للمعلم بإضافة نقاط الامتحان',           fr: 'Autoriser les enseignants à ajouter des points d\'examen' },
  checkEmailUniquenessForUsers: { ar: 'التحقق من عدم تكرار البريد الإلكتروني',         fr: 'Vérifier l\'unicité des e-mails utilisateurs' },
  applicationName:              { ar: 'اسم التطبيق',                                 fr: 'Nom de l\'application' },
  organizationName:             { ar: 'اسم المؤسسة',                                 fr: 'Nom de l\'organisation' },
  contactEmail:                 { ar: 'البريد الإلكتروني للتواصل',                    fr: 'E-mail de contact' },
  contactPhone:                 { ar: 'رقم الهاتف للتواصل',                           fr: 'Téléphone de contact' },
  address:                      { ar: 'العنوان',                                     fr: 'Adresse' },
  academicYearStart:            { ar: 'بداية السنة الدراسية',                          fr: 'Début de l\'année scolaire' },
  academicYearEnd:              { ar: 'نهاية السنة الدراسية',                          fr: 'Fin de l\'année scolaire' },
  maxStudentsPerClass:          { ar: 'الحد الأقصى للطلاب في الحلقة',                  fr: 'Nombre max d\'élèves par classe' },
  minAttendancePercentage:      { ar: 'نسبة الحضور الدنيا',                           fr: 'Taux de présence minimum (%)' },
  monthlyFee:                   { ar: 'الرسوم الشهرية',                               fr: 'Frais mensuels' },
  annualRegistrationFee:        { ar: 'رسوم التسجيل السنوية',                          fr: 'Frais d\'inscription annuels' },
  lateFeePercentage:            { ar: 'نسبة رسوم التأخير',                            fr: 'Taux de pénalité de retard (%)' },
  enableEmailNotifications:     { ar: 'تفعيل إشعارات البريد الإلكتروني',               fr: 'Activer les notifications e-mail' },
  enableSmsNotifications:       { ar: 'تفعيل إشعارات الرسائل النصية',                  fr: 'Activer les notifications SMS' },
  notificationLanguage:         { ar: 'لغة الإشعارات',                               fr: 'Langue des notifications' },
  maintenanceMode:              { ar: 'وضع الصيانة',                                 fr: 'Mode maintenance' },
  apiTimeout:                   { ar: 'مهلة الاتصال بالخادم',                          fr: 'Délai d\'attente API (ms)' },
  maxFileUploadSize:            { ar: 'الحد الأقصى لحجم الملف',                        fr: 'Taille max de téléchargement' },
  allowedFileTypes:             { ar: 'أنواع الملفات المسموحة',                         fr: 'Types de fichiers autorisés' },
  default_country:              { ar: 'الدولة الافتراضية لأرقام الهاتف',                fr: 'Pays par défaut (numéros de téléphone)' },
  default_timezone:             { ar: 'المنطقة الزمنية للجمعية',                        fr: 'Fuseau horaire de l\'association' },
};

const getParamLabel = (p: Param, isRTL: boolean): string => {
  const known = PARAM_NAMES[p.name];
  if (known) return isRTL ? known.ar : known.fr;
  return p.description || p.name;
};

/** Pays dédupliqués : `COUNTRIES` contient quelques doublons (CH, PT, SE, AT…). */
const UNIQUE_COUNTRIES = COUNTRIES.filter((c, i) => COUNTRIES.findIndex(x => x.code === c.code) === i);

/** Options du sélecteur de pays (le code courant est toujours inclus). */
const countryOptions = (value: string, lang: string) => (
  <>
    {value && !UNIQUE_COUNTRIES.some(c => c.code === value) && <option value={value}>{value}</option>}
    {UNIQUE_COUNTRIES.map(c => (
      <option key={c.code} value={c.code}>
        {c.flag} {c.name[lang as 'ar' | 'fr' | 'en'] || c.name.ar} (+{c.dialCode})
      </option>
    ))}
  </>
);

export const SystemParamsTab: React.FC<Props> = ({ isRTL, currentLanguage }) => {
  const [params, setParams]           = useState<Param[]>([]);
  const [loading, setLoading]         = useState(true);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editValue, setEditValue]     = useState('');
  const [saving, setSaving]           = useState(false);
  const [message, setMessage]         = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [settingInscYear, setSettingInscYear] = useState(false);

  // Aperçu « heure de l'association » pendant l'édition du fuseau : rafraîchi chaque seconde
  const [now, setNow] = useState<Date>(() => new Date());
  const dateLocale = currentLanguage === 'fr' ? 'fr-MA' : currentLanguage === 'en' ? 'en-US' : 'ar-MA';

  useEffect(() => {
    if (editingName !== 'default_timezone') return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [editingName]);

  // Fuseau : deux modes exclusifs (liste IANA OU décalage fixe) → une seule liste à la fois
  const [tzMode, setTzMode] = useState<'iana' | 'offset'>('iana');
  const lastIanaValue = useRef<string>('Africa/Casablanca');
  const isOffsetValue = (v: string) => /^[+-]\d{2}:?\d{2}$/.test((v ?? '').trim());

  const { years, inscriptionYear, setInscriptionYear } = useSchoolYear();
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const load = async () => {
    setLoading(true);
    const res: any = await api.getParameters();
    if (res?.success && Array.isArray(res.data)) setParams(res.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const visibleParams = params.filter((p) => !HIDDEN_FROM_TABLE.has(p.name));

  const startEdit = (p: Param) => {
    setEditingName(p.name);
    setEditValue(p.value);
    setMessage(null);
    // Le menu du fuseau s'ouvre directement dans le bon mode (IANA ou décalage fixe)
    if (p.name === 'default_timezone') {
      const isOffset = /^[+-]\d{2}:?\d{2}$/.test((p.value ?? '').trim());
      setTzMode(isOffset ? 'offset' : 'iana');
      if (!isOffset) lastIanaValue.current = p.value;
    }
  };

  const cancelEdit = () => setEditingName(null);

  const saveEdit = async (name: string) => {
    setSaving(true);
    const res: any = await api.updateParameter(name, { value: editValue });
    if (res?.success) {
      setParams(prev => prev.map(p => p.name === name ? { ...p, value: editValue } : p));
      setMessage({ type: 'success', text: L(isRTL, 'Paramètre mis à jour ✓', 'تم التحديث بنجاح ✓') });
      cancelEdit();
    } else {
      setMessage({ type: 'error', text: res?.message || 'Erreur' });
    }
    setSaving(false);
  };

  const cell: React.CSSProperties = { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', verticalAlign: 'middle' };
  const inputStyle: React.CSSProperties = { border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px 10px', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
  const btnSm = (bg: string, color = '#fff'): React.CSSProperties => ({ background: bg, color, border: 'none', borderRadius: '6px', padding: '5px 14px', fontSize: '12px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' as const });

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: '20px 0' }}>

      {message && (
        <div style={{ padding: '10px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', fontWeight: '500',
          background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          color:      message.type === 'success' ? '#16a34a'  : '#dc2626',
          border:     `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ float: isRTL ? 'left' : 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '14px' }}>×</button>
        </div>
      )}

      {/* Sélecteur de CONSULTATION (lecture seule) — change uniquement selectedYear (aucun impact sur is_current) */}
      <SchoolYearSystemInfo isRTL={isRTL} lang={currentLanguage as 'fr' | 'en' | 'ar'} />

      <SchoolYearSelector
        persistCurrent
        labels={{
          schoolYear: L(isRTL, 'Définir l\'année courante', 'تحديد السنة الدراسية الحالية'),
          fieldLabel: L(isRTL, 'Année courante', 'السنة الدراسية الحالية'),
          persistHint: L(
            isRTL,
            'Cette année devient l\'année courante pour tout le système.',
            'تُعتمد هذه السنة كسنة دراسية حالية في كل النظام.'
          ),
          saving: L(isRTL, 'Enregistrement…', 'جارٍ الحفظ…'),
          current: L(isRTL, 'Courante', 'حالية'),
          closed: L(isRTL, 'Clôturée (lecture seule)', 'مغلقة (قراءة فقط)'),
          newYear: L(isRTL, 'Créer une année', 'إنشاء سنة دراسية'),
          create: L(isRTL, 'Créer', 'إنشاء'),
          cancel: L(isRTL, 'Annuler', 'إلغاء'),
          creating: L(isRTL, 'Création…', 'جارٍ الإنشاء…'),
          startDate: L(isRTL, 'Début', 'البداية'),
          endDate: L(isRTL, 'Fin', 'النهاية'),
          calendarHint: L(
            isRTL,
            'Par défaut : 1er septembre → 31 août (modifiable)',
            'افتراضياً: 1 سبتمبر → 31 أغسطس (قابل للتعديل)'
          ),
          closeYear: L(isRTL, 'Clôturer l\'année courante', 'إنهاء السنة الحالية'),
          closeYearTitle: L(isRTL, 'Clôturer l\'année scolaire', 'إغلاق السنة الدراسية'),
          closeYearHelp: L(
            isRTL,
            'L\'année courante passera en lecture seule. Choisissez l\'année à activer.',
            'ستصبح السنة الحالية للقراءة فقط. اختر السنة التي تُفعَّل.'
          ),
          activateYear: L(isRTL, 'Activer l\'année', 'تفعيل السنة'),
          confirmClose: L(isRTL, 'Clôturer et activer', 'إغلاق وتفعيل'),
          closing: L(isRTL, 'Clôture…', 'جارٍ الإغلاق…'),
          closeError: L(isRTL, 'Erreur lors de la clôture', 'خطأ أثناء الإغلاق'),
          closeYearNeedNew: L(
            isRTL,
            'Créez d\'abord la nouvelle année (« Créer une année »), puis clôturez.',
            'أنشئ السنة الجديدة أولاً (« إنشاء سنة دراسية »)، ثم أنهِ السنة الحالية.'
          ),
        }}
      />

      {/* ── Année ouverte aux inscriptions ── */}
      <SchoolYearCard
        icon="📋"
        iconBg="#dcfce7"
        titleColor="#166534"
        title={L(isRTL, 'Année ouverte aux inscriptions', 'السنة المفتوحة للتسجيل')}
        hint={L(
          isRTL,
          'Les demandes d\'inscription publiques sont rattachées à cette année.',
          'تُربط طلبات التسجيل العمومية بهذه السنة.'
        )}
        isRTL={isRTL}
        fieldId="school-year-inscription"
        field={
          <select
            id="school-year-inscription"
            value={inscriptionYear?.id ?? ''}
            onChange={async (e) => {
              const id = Number(e.target.value);
              const yr = years.find(y => y.id === id);
              if (!yr) return;
              setSettingInscYear(true);
              await setInscriptionYear(yr);
              setSettingInscYear(false);
            }}
            disabled={settingInscYear || years.length === 0}
            style={{
              ...schoolYearFieldStyle,
              cursor: settingInscYear ? 'wait' : 'pointer',
              backgroundColor: settingInscYear ? '#f9fafb' : '#fff',
            }}
          >
            {years.filter(y => {
              if (y.status === 'closed') return false;
              return !y.end_date || y.end_date >= todayStr;
            }).map(y => (
              <option key={y.id} value={y.id}>{y.label}</option>
            ))}
          </select>
        }
        actions={
          <>
            {settingInscYear && (
              <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600 }}>
                {L(isRTL, 'Enregistrement…', 'جارٍ الحفظ…')}
              </span>
            )}
            {inscriptionYear && !settingInscYear && (
              <SchoolYearStatusChip
                label={`${L(isRTL, 'Inscriptions ouvertes pour ', 'التسجيل مفتوح لـ ')}${inscriptionYear.label}`}
              />
            )}
          </>
        }
      />

      <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
        {L(isRTL, 'Autres paramètres de l\'application', 'معاملات التطبيق الأخرى')}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>{L(isRTL, 'Chargement…', 'جارٍ التحميل…')}</div>
      ) : visibleParams.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>{L(isRTL, 'Aucun paramètre', 'لا توجد معاملات')}</div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {[L(isRTL, 'Paramètre', 'المعامل'), L(isRTL, 'Valeur', 'القيمة'), L(isRTL, 'Actions', 'إجراءات')].map((h, i) => (
                  <th key={i} style={{ ...cell, fontWeight: '600', color: '#374151', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: i === 2 ? 'center' : (isRTL ? 'right' : 'left'), borderBottom: '2px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleParams.map(p => (
                <tr key={p.name} style={{ background: editingName === p.name ? '#faf5ff' : 'white' }}
                  onMouseEnter={e => { if (editingName !== p.name) (e.currentTarget as HTMLElement).style.background = '#fafafa'; }}
                  onMouseLeave={e => { if (editingName !== p.name) (e.currentTarget as HTMLElement).style.background = 'white'; }}>
                  <td style={{ ...cell, fontWeight: '600', color: '#1f2937', width: '45%' }}>
                    {getParamLabel(p, isRTL)}
                  </td>
                  <td style={{ ...cell, width: '40%' }}>
                    {editingName === p.name ? (
                      p.name === 'default_country' ? (
                        <select
                          autoFocus
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveEdit(p.name); if (e.key === 'Escape') cancelEdit(); }}
                          style={{ ...inputStyle, cursor: 'pointer' }}
                        >
                          {countryOptions(editValue, currentLanguage)}
                        </select>
                      ) : p.name === 'default_timezone' ? (
                        <>
                          {/* Deux modes exclusifs : une seule liste à la fois (IANA OU décalage fixe) */}
                          <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                            {([
                              { id: 'iana' as const,   label: L(isRTL, 'Fuseau horaire', 'منطقة زمنية') },
                              { id: 'offset' as const, label: L(isRTL, 'Décalage fixe (UTC)', 'فرق ثابت (UTC)') },
                            ]).map(m => (
                              <button
                                key={m.id}
                                type="button"
                                onClick={() => {
                                  if (m.id === tzMode) return;
                                  if (m.id === 'offset' && !isOffsetValue(editValue)) {
                                    setEditValue(currentOffsetOf(editValue) ?? '+00:00');
                                  } else if (m.id === 'iana' && isOffsetValue(editValue)) {
                                    setEditValue(lastIanaValue.current);
                                  }
                                  setTzMode(m.id);
                                }}
                                style={{
                                  padding: '4px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer',
                                  border: '1px solid ' + (tzMode === m.id ? '#5e35b1' : '#d1d5db'),
                                  background: tzMode === m.id ? '#5e35b1' : '#fff',
                                  color: tzMode === m.id ? '#fff' : '#374151',
                                }}
                              >
                                {m.label}
                              </button>
                            ))}
                          </div>
                          <select
                            autoFocus
                            value={editValue}
                            onChange={e => {
                              const v = e.target.value;
                              if (!isOffsetValue(v)) lastIanaValue.current = v;
                              setEditValue(v);
                            }}
                            onKeyDown={e => { if (e.key === 'Enter') saveEdit(p.name); if (e.key === 'Escape') cancelEdit(); }}
                            style={{ ...inputStyle, fontFamily: 'monospace', cursor: 'pointer' }}
                            dir="ltr"
                          >
                            {/* Valeur courante hors liste du mode (sécurité) */}
                            {editValue && !(tzMode === 'iana' ? getIanaTimezones() : FIXED_OFFSET_OPTIONS).includes(editValue) && (
                              <option value={editValue}>{editValue}</option>
                            )}
                            {tzMode === 'iana'
                              ? getIanaTimezones().map(tz => <option key={tz} value={tz}>{tz}</option>)
                              : FIXED_OFFSET_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                          {/* Aperçu en direct : l'admin compare avec l'heure réelle et ajuste si besoin */}
                          <div style={{ marginTop: '6px', fontSize: '12px', color: '#6b7280', fontFamily: 'inherit' }} dir={isRTL ? 'rtl' : 'ltr'}>
                            🕒 {L(isRTL, 'Il est actuellement', 'الوقت الآن')}{' '}
                            <b style={{ color: '#059669', fontFamily: 'monospace' }}>{formatTimeInTz(now, editValue, dateLocale)}</b>{' '}
                            {L(isRTL, 'à l\'association', 'في الجمعية')}
                            {(() => {
                              const zone = isOffsetValue(editValue) ? (lastIanaValue.current || 'Africa/Casablanca') : editValue;
                              const suggested = currentOffsetOf(zone);
                              if (!suggested || suggested === editValue) return null;
                              return (
                                <button
                                  type="button"
                                  onClick={() => setEditValue(suggested)}
                                  title={L(isRTL, `D'après ce navigateur (${zone}) — à vérifier`, `حسب هذا المتصفح (${zone}) — للتحقق`)}
                                  style={{ marginInlineStart: '8px', background: 'none', border: 'none', color: '#5e35b1', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', padding: 0, fontFamily: 'inherit' }}
                                >
                                  {L(isRTL, `Déduire d'après ce navigateur (${suggested})`, `استنتاج من هذا المتصفح (${suggested})`)}
                                </button>
                              );
                            })()}
                          </div>
                        </>
                      ) : (
                        <input
                          autoFocus
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveEdit(p.name); if (e.key === 'Escape') cancelEdit(); }}
                          style={{ ...inputStyle, fontFamily: 'monospace' }}
                          dir="ltr"
                        />
                      )
                    ) : (
                      <span
                        style={{ fontFamily: p.name === 'default_country' ? undefined : 'monospace', color: '#374151' }}
                        dir={p.name === 'default_country' ? 'auto' : 'ltr'}
                      >
                        {formatParamValue(p.name, p.value, currentLanguage)}
                      </span>
                    )}
                  </td>
                  <td style={{ ...cell, textAlign: 'center', width: '15%', whiteSpace: 'nowrap' }}>
                    {editingName === p.name ? (
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                        <button onClick={() => saveEdit(p.name)} disabled={saving} style={btnSm('#059669')}>{saving ? '…' : L(isRTL, 'Sauver', 'حفظ')}</button>
                        <button onClick={cancelEdit} style={btnSm('#6b7280')}>{L(isRTL, 'Annuler', 'إلغاء')}</button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(p)} style={btnSm('#5e35b1')}>{L(isRTL, 'Modifier', 'تعديل')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};